import { useEffect, useRef, useState } from 'react'
import { getSpeechRecognitionLocale } from '../utils/chatUiLocale'

/** English wake phrase (legacy). */
const WAKE_PHRASE_EN = 'open voice assistant'
/** French wake phrase (legacy). */
const WAKE_PHRASE_FR = "ouvrir l'assistant vocal"

/**
 * Phrases for launcher tooltip (hands-free open). Wake logic still supports additional
 * phrases; these are the user-visible EN/FR examples.
 */
export const WAKE_PHRASE_HINT = {
  openChatEn: 'Open voice assistant',
  openChatFr: "Ouvrir l'assistant vocal",
} as const

function normalizeForWake(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const NORM_EN = normalizeForWake(WAKE_PHRASE_EN)
const NORM_FR = normalizeForWake(WAKE_PHRASE_FR)

/**
 * Mic wake when the chat panel is open — normalized full-phrase substrings.
 * Covers intended “Hey Medico” plus common STT spellings (mediko, medical, medio).
 */
const MIC_WAKE_HEY_SUBSTRINGS = [
  normalizeForWake('hey medico'),
  normalizeForWake('hey mediko'),
  normalizeForWake('hey medical'),
  normalizeForWake('hey medio'),
] as const

/** Name part of the wake phrase — STT varies (medico / mediko / medio / medical / medic…). */
function hasMedicoFamilyToken(n: string): boolean {
  return (
    n.includes('medico') ||
    n.includes('mediko') ||
    n.includes('medio') ||
    n.includes('medical') ||
    /\bmedic\b/.test(n)
  )
}

function hasHeyLike(n: string): boolean {
  return /\b(hey|hi|hay|hei)\b/.test(n) || /(^|\s)(hey|hi|hay)\s/.test(n)
}

/** When the chat panel is closed — opens the assistant (legacy). */
function transcriptContainsLegacyOpenAssistant(text: string): boolean {
  const n = normalizeForWake(text)
  if (n.includes(NORM_EN) || n.includes(NORM_FR)) return true
  // Common STT variant of "assistant" misspelling spoken by users.
  if (n.includes('open voice assisstant')) return true
  if (
    n.includes('ouvre assistant vocal') ||
    n.includes('ouvrir assistant vocal') ||
    n.includes('ouvrir l assistant vocal') ||
    n.includes('ouvre l assistant vocal')
  ) {
    return true
  }

  const hasOpen = /\b(open|launch|start)\b/.test(n) || n.includes('open ')
  const hasVoice = n.includes('voice')
  /** "assist" catches assistant, assisstant, assistance, etc. */
  const hasAssistant = n.includes('assist')

  if (hasOpen && hasVoice && hasAssistant) return true

  // Also accept direct chat-open intents used in voice commands.
  const hasChat = /\b(chat|panel|window|interface)\b/.test(n)
  if (hasOpen && (hasAssistant || hasVoice || hasChat)) return true

  const hasOuvrir = n.includes('ouvrir') || n.includes('ouvre') || n.includes('lancer')
  const hasVocal = n.includes('vocal')
  const hasChatFr = /\b(chat|fenetre|interface|panneau)\b/.test(n)
  if (hasOuvrir && hasAssistant && hasVocal) return true
  if (hasOuvrir && (hasAssistant || hasVocal || hasChatFr)) return true

  return false
}

/**
 * When the chat panel is open — closes only on explicit close-assistant phrases.
 * Intentionally strict so generic "close it/file" commands never close the chat panel.
 */
function transcriptContainsCloseAssistant(text: string): boolean {
  const n = normalizeForWake(text)
  if (
    n.includes('close voice assistant') ||
    n.includes('close the voice assistant') ||
    n.includes('close voice assisstant')
  ) {
    return true
  }
  if (
    n.includes('fermer assistant vocal') ||
    n.includes('ferme assistant vocal') ||
    n.includes('fermer l assistant vocal') ||
    n.includes('ferme l assistant vocal')
  ) {
    return true
  }

  const hasCloseEn = /\b(close)\b/.test(n)
  const hasCloseFr = /\b(fermer|ferme)\b/.test(n)
  const hasVoiceEn = /\bvoice\b/.test(n)
  const hasVoiceFr = /\bvocal\b/.test(n)
  // "assist" catches assistant / assisstant variants but not generic close-file phrasing.
  const hasAssistant = n.includes('assist')

  if (hasAssistant && ((hasCloseEn && hasVoiceEn) || (hasCloseFr && hasVoiceFr))) return true
  return false
}

/** When the chat panel is open — starts the mic (“Hey Medico” and close STT variants). */
function transcriptContainsHeyMedico(text: string): boolean {
  const n = normalizeForWake(text)
  for (const p of MIC_WAKE_HEY_SUBSTRINGS) {
    if (n.includes(p)) return true
  }
  if (hasMedicoFamilyToken(n) && hasHeyLike(n)) return true
  return false
}

function preferredWakeRecognitionLang(): string {
  return getSpeechRecognitionLocale().startsWith('fr') ? 'fr-FR' : 'en-US'
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognition) | null {
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null
}

const WAKE_DEBOUNCE_MS = 2500
const WAKE_RESTART_MS = 400
const WAKE_RECOVERABLE_RETRY_MS = 2500

export type WakePhraseUiPanel = {
  tone: 'info' | 'error'
  main: string
  sub?: string
}

export type UseWakePhraseRecognitionArgs = {
  suspend: boolean
  panelOpen: boolean
  onOpenAssistant: () => void
  onCloseAssistant: () => void
  onHeyMedico: () => void
}

export type UseWakePhraseRecognitionResult = {
  /** Non-null only for wake listener errors (or unsupported environment). */
  panel: WakePhraseUiPanel | null
  /** Dev-only: latest transcript fragment heard by browser STT for wake matching. */
  debugHeard: string | null
}

type WakeMicFailure = {
  fatal: boolean
  panel: WakePhraseUiPanel
  retryMs?: number
}

function describeWakeMicFailure(error: unknown): WakeMicFailure {
  const name =
    (error as DOMException | null)?.name ||
    (error instanceof Error ? error.name : '') ||
    String(error || '')
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return {
      fatal: true,
      panel: { tone: 'error', main: 'Microphone permission was denied for wake listening.' },
    }
  }
  if (name === 'NotFoundError') {
    return {
      fatal: true,
      panel: { tone: 'error', main: 'No microphone was found for wake listening.' },
    }
  }
  if (name === 'SecurityError') {
    return {
      fatal: true,
      panel: { tone: 'error', main: 'Microphone access is blocked for this page.' },
    }
  }
  if (name === 'NotReadableError') {
    return {
      fatal: false,
      retryMs: WAKE_RECOVERABLE_RETRY_MS,
      panel: {
        tone: 'error',
        main: 'Microphone is busy or temporarily unavailable.',
        sub: 'Wake listening will retry automatically.',
      },
    }
  }
  return {
    fatal: true,
    panel: { tone: 'error', main: 'Microphone access for wake listening is unavailable.' },
  }
}

function describeWakeRecognitionError(code: string): {
  fatal: boolean
  panel: WakePhraseUiPanel | null
  retryMs?: number
} {
  switch (code) {
    case 'no-speech':
    case 'aborted':
      return { fatal: false, panel: null }
    case 'audio-capture':
      return {
        fatal: false,
        retryMs: WAKE_RECOVERABLE_RETRY_MS,
        panel: {
          tone: 'error',
          main: 'Microphone input became unavailable.',
          sub: 'Wake listening will retry automatically.',
        },
      }
    case 'network':
      return {
        fatal: false,
        retryMs: WAKE_RECOVERABLE_RETRY_MS,
        panel: {
          tone: 'error',
          main: 'Speech recognition lost connection.',
          sub: 'Wake listening will retry automatically.',
        },
      }
    case 'not-allowed':
      return {
        fatal: true,
        panel: { tone: 'error', main: 'Microphone permission was denied for wake listening.' },
      }
    case 'service-not-allowed':
      return {
        fatal: true,
        panel: { tone: 'error', main: 'Speech recognition is not allowed in this browser.' },
      }
    case 'language-not-supported':
      return {
        fatal: true,
        panel: { tone: 'error', main: 'Speech language is not supported for wake listening.' },
      }
    default:
      return {
        fatal: false,
        retryMs: WAKE_RECOVERABLE_RETRY_MS,
        panel: {
          tone: 'error',
          main: 'Wake listening hit a temporary speech recognition error.',
          sub: 'It will retry automatically.',
        },
      }
  }
}

function isWakeDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const host = (window.location.hostname || '').toLowerCase()
  const qp = new URLSearchParams(window.location.search)
  if (qp.get('wake_debug') === '1') return true
  if (window.localStorage?.getItem('vnw:wake_debug') === '1') return true
  return host === 'localhost' || host === '127.0.0.1'
}

/**
 * Continuous SpeechRecognition listening for fixed wake phrases.
 * Starts after microphone permission is granted; restarts automatically when the engine stops.
 */
export function useWakePhraseRecognition({
  suspend,
  panelOpen,
  onOpenAssistant,
  onCloseAssistant,
  onHeyMedico,
}: UseWakePhraseRecognitionArgs): UseWakePhraseRecognitionResult {
  const onOpenAssistantRef = useRef(onOpenAssistant)
  onOpenAssistantRef.current = onOpenAssistant
  const onCloseAssistantRef = useRef(onCloseAssistant)
  onCloseAssistantRef.current = onCloseAssistant
  const onHeyMedicoRef = useRef(onHeyMedico)
  onHeyMedicoRef.current = onHeyMedico
  const panelOpenRef = useRef(panelOpen)
  panelOpenRef.current = panelOpen

  const lastWakeAtRef = useRef(0)
  const reportedUnsupportedRef = useRef(false)

  const [panel, setPanel] = useState<WakePhraseUiPanel | null>(null)
  const [debugHeard, setDebugHeard] = useState<string | null>(null)
  const debugEnabled = isWakeDebugEnabled()

  useEffect(() => {
    if (suspend) {
      setPanel(null)
      setDebugHeard(null)
      return
    }

    const SR = getSpeechRecognitionCtor()
    if (!SR) {
      if (!reportedUnsupportedRef.current) {
        reportedUnsupportedRef.current = true
        setPanel({
          tone: 'error',
          main: 'Speech recognition is not available in this browser.',
        })
      }
      return
    }
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      if (!reportedUnsupportedRef.current) {
        reportedUnsupportedRef.current = true
        setPanel({
          tone: 'error',
          main: 'Speech recognition requires HTTPS.',
        })
      }
      return
    }

    let cancelled = false
    /** If true, onend must not auto-restart (mic denied, blocked service, etc.). */
    let fatalStop = false
    let recognition: SpeechRecognition | null = null
    let restartTimer: ReturnType<typeof setTimeout> | null = null
    let restartDelayMs = WAKE_RESTART_MS

    const clearRestartTimer = () => {
      if (restartTimer) {
        clearTimeout(restartTimer)
        restartTimer = null
      }
    }

    const stopRec = () => {
      clearRestartTimer()
      if (recognition) {
        try {
          recognition.onresult = null
          recognition.onend = null
          recognition.onerror = null
          recognition.onstart = null
          recognition.abort()
        } catch {
          // ignore
        }
        recognition = null
      }
    }

    const scheduleRestart = (delayMs = restartDelayMs) => {
      if (cancelled || suspend || fatalStop) return
      clearRestartTimer()
      restartTimer = window.setTimeout(() => {
        restartTimer = null
        if (!cancelled && !suspend && !fatalStop) void begin()
      }, delayMs)
    }

    const beginIfRecoverable = () => {
      if (cancelled || suspend || fatalStop || recognition) return
      clearRestartTimer()
      void begin()
    }

    const begin = async () => {
      if (cancelled || suspend) return
      fatalStop = false
      restartDelayMs = WAKE_RESTART_MS

      setPanel(null)

      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setPanel({
          tone: 'error',
          main: 'Microphone access is not available.',
        })
        return
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach((t) => t.stop())
      } catch (e) {
        const failure = describeWakeMicFailure(e)
        fatalStop = failure.fatal
        setPanel(failure.panel)
        if (!failure.fatal) {
          scheduleRestart(failure.retryMs ?? WAKE_RESTART_MS)
        }
        return
      }

      if (cancelled || suspend) return

      const rec = new SR()
      rec.continuous = true
      rec.interimResults = true
      rec.lang = preferredWakeRecognitionLang()
      rec.maxAlternatives = 3

      rec.onstart = () => {
        restartDelayMs = WAKE_RESTART_MS
        setPanel(null)
      }

      rec.onresult = (ev: SpeechRecognitionEvent) => {
        let accumulated = ''
        let latestHeard = ''
        for (let i = 0; i < ev.results.length; i++) {
          const result = ev.results[i]
          if (!result) continue
          if (result[0]?.transcript) latestHeard += ` ${result[0].transcript}`
          for (let j = 0; j < result.length; j++) {
            accumulated += ` ${result[j]?.transcript ?? ''}`
          }
        }
        if (debugEnabled) {
          const heard = (latestHeard || accumulated).replace(/\s+/g, ' ').trim()
          if (heard) setDebugHeard(heard.slice(0, 220))
        }
        const open = panelOpenRef.current
        const closeAssistant = open ? transcriptContainsCloseAssistant(accumulated) : false
        const wakeMic = open ? transcriptContainsHeyMedico(accumulated) : false
        const openAssistant = !open ? transcriptContainsLegacyOpenAssistant(accumulated) : false
        const matches = closeAssistant || wakeMic || openAssistant
        if (!matches) return
        const now = Date.now()
        if (now - lastWakeAtRef.current < WAKE_DEBOUNCE_MS) return
        lastWakeAtRef.current = now
        try {
          rec.abort()
        } catch {
          // ignore
        }
        queueMicrotask(() => {
          if (closeAssistant) {
            onCloseAssistantRef.current()
          } else if (open) {
            onHeyMedicoRef.current()
          } else {
            onOpenAssistantRef.current()
          }
        })
      }

      rec.onend = () => {
        recognition = null
        if (!cancelled && !suspend && !fatalStop) scheduleRestart()
      }

      rec.onerror = (ev: SpeechRecognitionErrorEvent) => {
        const failure = describeWakeRecognitionError(ev.error)
        fatalStop = failure.fatal
        restartDelayMs = failure.retryMs ?? WAKE_RESTART_MS
        if (failure.panel) {
          setPanel(failure.panel)
        }
      }

      recognition = rec
      try {
        rec.start()
      } catch (e) {
        setPanel({
          tone: 'error',
          main: 'Could not start wake listening.',
          sub: 'Retrying automatically.',
        })
        restartDelayMs = WAKE_RECOVERABLE_RETRY_MS
        scheduleRestart()
      }
    }

    const onFocus = () => beginIfRecoverable()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') beginIfRecoverable()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    const mediaDevices = navigator.mediaDevices
    const canWatchDeviceChanges = typeof mediaDevices?.addEventListener === 'function'
    const onDeviceChange = () => beginIfRecoverable()
    if (canWatchDeviceChanges) {
      mediaDevices.addEventListener('devicechange', onDeviceChange)
    }

    void begin()

    return () => {
      cancelled = true
      if (canWatchDeviceChanges) {
        mediaDevices.removeEventListener('devicechange', onDeviceChange)
      }
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
      stopRec()
    }
  }, [suspend, panelOpen])

  if (suspend) {
    return { panel: null, debugHeard: null }
  }

  return { panel, debugHeard: debugEnabled ? debugHeard : null }
}
